import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { API_ENDPOINTS } from '../../../shared/constants/api.constants';
import {
  AddJcCostRequest,
  AddJcCommittedRequest,
  AddJcAccrualRequest,
  AddJcTimeRequest,
  AddJcInvoiceRequest,
  AddJcLineRequest,
  EditJcEntryRequest,
  JcAttachment,
  JcBudgetSource,
  JcDocument,
  JcCostType,
  JcLine,
  JcProjectStatus,
  JcReviewChange,
  JcSection,
  JcLineHistory,
  JcNote,
  JcTransaction,
  JobCostingProjectDetail,
  JobCostingProjectSummary,
  QboSyncPreview,
  QboSyncRun,
  UpdateJcLineBudgetRequest,
} from '../../models/job-costing.models';
import { formatMoney } from './job-costing.calc';
import { JobCostingApi } from './job-costing.api';

/**
 * Job costing over `api/shard/admin/job-cost/*` (JobCostController). Request and response shapes follow the
 * backend's ManualLedgerEntryRequest / JobCostLedgerResponse / JobCostLedgerEntryResponse.
 */
@Injectable()
export class JobCostingHttpApi extends JobCostingApi {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl;
  private readonly endpoints = API_ENDPOINTS.JOB_COSTING;

  getProjects(): Observable<JobCostingProjectSummary[]> {
    return this.landingPage(1).pipe(
      switchMap(first => {
        const totalPages = first.totalPages ?? 1;
        if (totalPages <= 1) {
          return of(first.items.map(mapLandingRow));
        }
        const rest = Array.from({ length: totalPages - 1 }, (_, index) => this.landingPage(index + 2));
        return forkJoin(rest).pipe(
          map(pages => [first, ...pages].flatMap(page => page.items.map(mapLandingRow))),
        );
      }),
    );
  }

  private landingPage(pageNumber: number): Observable<LedgerPage<JobCostLandingRow>> {
    const params = new HttpParams().set('pageNumber', String(pageNumber)).set('pageSize', '200');
    return this.http
      .get<ApiEnvelope<JobCostLanding>>(`${this.base}${this.endpoints.PROJECTS}`, { params })
      .pipe(map(response => response.data?.projects ?? { items: [], totalPages: 0 }));
  }

  getProjectDetail(projectId: number, asOfDate?: string): Observable<JobCostingProjectDetail> {
    let params = new HttpParams().set('projectId', String(projectId));
    if (asOfDate) {
      params = params.set('asOfDate', asOfDate);
    }
    return this.http
      .get<ApiEnvelope<JobCostRollup>>(`${this.base}${this.endpoints.PROJECT_ROLLUP}`, { params })
      .pipe(map(response => mapRollup(response.data, projectId)));
  }

  getLineTransactions(lineId: string): Observable<JcTransaction[]> {
    return this.ledgerEntries(lineId, 'EST_LINEITEM').pipe(
      map(entries => mapEntries(entries.filter(entry => entry.entryClass !== 'BUDGET'))),
    );
  }

  getLineDocuments(lineId: string): Observable<JcDocument[]> {
    const params = new HttpParams().set('ledgerId', lineId);
    return forkJoin({
      files: this.http
        .get<ApiEnvelope<LedgerAttachment[]>>(`${this.base}${this.endpoints.LINE_DOCUMENTS}`, { params })
        .pipe(map(response => response.data ?? [])),
      entries: this.ledgerEntries(lineId),
    }).pipe(map(({ files, entries }) => mapDocuments(files, entries)));
  }

  uploadLineDocument(lineId: string, file: File): Observable<void> {
    return this.uploadAttachment(lineId, file).pipe(map(() => undefined));
  }

  deleteLineDocument(attachmentId: string): Observable<void> {
    const params = new HttpParams().set('attachmentId', attachmentId);
    return this.http
      .delete(`${this.base}${this.endpoints.DOCUMENT_DELETE}`, { params })
      .pipe(map(() => undefined));
  }

  getLineNotes(lineId: string): Observable<JcNote[]> {
    const params = new HttpParams().set('ledgerId', lineId).set('pageNumber', '1').set('pageSize', '100');
    return this.http
      .get<ApiEnvelope<LedgerPage<LedgerNote>>>(`${this.base}${this.endpoints.LINE_NOTES}`, { params })
      .pipe(map(response => (response.data?.items ?? []).map(mapNote)));
  }

  /** Budget records and activity from the line's entries, plus the Cost Inbox review changes behind posted receipts (FR-13). */
  getLineHistory(lineId: string): Observable<JcLineHistory> {
    return this.ledgerEntries(lineId).pipe(
      switchMap(entries => {
        const fromInbox = entries.filter(
          entry => (entry.entityType ?? '').toUpperCase() === 'COST_INBOX_LINE' && !entry.isReversal,
        );
        if (!fromInbox.length) {
          return of(mapHistory(entries, []));
        }
        return forkJoin(fromInbox.map(entry => this.entryReviewChanges(entry))).pipe(
          map(changes => mapHistory(entries, changes.flat())),
        );
      }),
    );
  }

  addCost(request: AddJcCostRequest): Observable<JcTransaction> {
    const credit = !!request.isCredit;
    const matched = !credit && request.accrualChoice && request.accrualChoice !== 'none' ? toId(request.accrualChoice) : null;
    return this.postEntry(request.lineId, request.file, {
      projectId: request.projectId,
      costCodeId: request.costCodeId,
      entryType: 'ACTUAL',
      documentType: credit ? null : 'EXPENSE',
      isCredit: credit,
      // A credit is entered positive; the API posts it negative (FR-08).
      amount: Math.abs(request.amount),
      documentDate: request.documentDate,
      vendor: request.vendor || null,
      memo: [request.note ?? '', dueNote(credit ? 'Paid' : request.payment, request.dueDate)].filter(Boolean).join('. ') || null,
      isPaid: !credit && request.payment === 'Paid',
      appliedToEntryId: toId(request.appliedToEntryId),
      creditsEntryId: credit ? toId(request.creditsEntryId) : null,
      ...accrualChoice(credit, request.accrualChoice, matched),
    });
  }

  addInvoice(request: AddJcInvoiceRequest): Observable<JcTransaction> {
    const matched = toId(request.matchedAccrualEntryId);
    return this.postEntry(request.lineId, request.file, {
      projectId: request.projectId,
      costCodeId: request.costCodeId,
      entryType: 'ACTUAL',
      documentType: 'INVOICE',
      amount: request.amount,
      documentDate: request.documentDate,
      vendor: request.vendor || null,
      externalRef: request.invoiceNumber || null,
      memo: [request.note ?? '', dueNote(request.payment, request.dueDate)].filter(Boolean).join('. ') || null,
      isPaid: request.payment === 'Paid',
      appliedToEntryId: toId(request.appliedToEntryId),
      ...accrualChoice(false, matched ? String(matched) : 'none', matched),
    });
  }

  addCommitted(request: AddJcCommittedRequest): Observable<JcTransaction> {
    return this.postEntry(request.lineId, request.file ?? null, {
      projectId: request.projectId,
      costCodeId: request.costCodeId,
      entryType: 'COMMITMENT',
      amount: request.amount,
      documentDate: request.effectiveDate ?? todayIsoDate(),
      vendor: request.vendor || null,
      externalRef: request.poNumber || null,
      overBudgetReason: request.overBudgetReason || null,
    });
  }

  addAccrual(request: AddJcAccrualRequest): Observable<JcTransaction> {
    return this.postEntry(request.lineId, null, {
      projectId: request.projectId,
      costCodeId: request.costCodeId,
      entryType: 'ACCRUAL',
      amount: request.amount,
      documentDate: request.serviceStart,
      serviceStartDate: request.serviceStart,
      serviceEndDate: request.serviceEnd,
      vendor: request.vendor || null,
      description: request.description,
      appliedToEntryId: toId(request.appliedToEntryId),
    });
  }

  /** Manual labor for teams outside AIO timesheet approval — the API computes hours × rate (FR-10). */
  addTime(request: AddJcTimeRequest): Observable<JcTransaction> {
    return this.postEntry(request.lineId, null, {
      projectId: request.projectId,
      costCodeId: request.costCodeId,
      entryType: 'TIME',
      documentDate: request.documentDate,
      workerName: request.employee,
      hours: request.hours,
      rate: request.rate,
    });
  }

  reverseEntry(entryId: string, reason: string): Observable<void> {
    return this.http
      .post<ApiEnvelope<unknown>>(`${this.base}${this.endpoints.REVERSE_ENTRY}`, {
        entryId: Number(entryId),
        reason,
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => {
          if (isAlreadyReversedError(err)) {
            return of(undefined);
          }
          throw err;
        }),
      );
  }

  relieveAccrual(accrualEntryId: string, actualEntryId: string): Observable<void> {
    return this.http
      .post(`${this.base}${this.endpoints.RELIEVE_ACCRUAL}`, {
        accrualEntryId: Number(accrualEntryId),
        actualEntryId: Number(actualEntryId),
      })
      .pipe(map(() => undefined));
  }

  /** Reverse + replacement in one transaction on the API; a new document is filed on the line first. */
  editEntry(request: EditJcEntryRequest): Observable<JcTransaction> {
    const attach: Observable<number | null> = request.file ? this.uploadAttachment(request.lineId, request.file) : of(null);
    return attach.pipe(
      switchMap(attachmentId =>
        this.http.post<ApiEnvelope<EditEntryResult>>(`${this.base}${this.endpoints.EDIT_ENTRY}`, {
          clientRequestId: crypto.randomUUID(),
          entryId: Number(request.transactionId),
          newAmount: request.amount,
          newContractPrice: request.contractPrice ?? null,
          newAttachmentId: attachmentId,
          reason: request.reason || null,
        }),
      ),
      map(response => {
        const entry = response.data?.replacementEntry;
        if (!entry) {
          throw new Error('The edited entry was not returned');
        }
        return mapEntryWithContext(entry);
      }),
    );
  }

  markEntryPaid(entryId: string): Observable<void> {
    return this.http
      .post(`${this.base}${this.endpoints.MARK_ENTRY_PAID}`, { entryId: Number(entryId), isPaid: true })
      .pipe(map(() => undefined));
  }

  addNote(lineId: string, text: string): Observable<JcNote> {
    return this.http
      .post<ApiEnvelope<LedgerNote>>(`${this.base}${this.endpoints.LINE_NOTES}`, {
        entityType: 'JOB_COST_LEDGER',
        entityId: Number(lineId),
        text,
      })
      .pipe(map(response => mapNote(response.data)));
  }

  /** + Add Line, and "Set budget" on an unbudgeted line (the API converts it in place with its costs). */
  addLine(request: AddJcLineRequest): Observable<JcLine> {
    return this.http
      .post<ApiEnvelope<JobCostRollupLine>>(`${this.base}${this.endpoints.ADD_LINE}`, {
        clientRequestId: crypto.randomUUID(),
        projectId: request.projectId,
        costCodeId: request.costCodeId,
        jobCostBudget: request.budgetCost,
        contractPrice: request.contractPrice,
      })
      .pipe(
        map(response => {
          const line = response.data;
          if (!line) {
            throw new Error('The line was not returned');
          }
          return mapLine(line, String(line.scopeTypeId ?? 'unsectioned'));
        }),
      );
  }

  /** Typed budget on an Added line edits the line's own BUDGET record — cost and client $ together (FR-05). */
  updateLineBudget(request: UpdateJcLineBudgetRequest): Observable<void> {
    if (!(request.budgetCost > 0)) {
      return throwError(() => new Error('Set the Job Cost Budget first — an added line needs a cost before a Contract Price.'));
    }
    return this.ledgerEntries(request.lineId).pipe(
      switchMap(entries => {
        const record = entries.find(
          entry => entry.entryClass === 'BUDGET' && (entry.entityType ?? '').toUpperCase() === 'MANUAL' && !entry.isReversal && !entry.reversedByEntryId,
        );
        if (!record) {
          return throwError(() => new Error('This line has no budget record to edit.'));
        }
        return this.http.post<ApiEnvelope<EditEntryResult>>(`${this.base}${this.endpoints.EDIT_ENTRY}`, {
          clientRequestId: crypto.randomUUID(),
          entryId: record.entryId,
          newAmount: request.budgetCost,
          newContractPrice: request.contractPrice,
        });
      }),
      map(() => undefined),
    );
  }

  getQboPreview(_projectId: number): Observable<QboSyncPreview> {
    // Job cost has no QuickBooks preview route yet; the Sync to QBO button is disabled.
    return of(emptyQboPreview);
  }

  runQboSync(projectId: number): Observable<QboSyncRun> {
    return this.http.post<QboSyncRun>(`${this.base}${this.endpoints.QBO_SYNC(projectId)}`, {});
  }

  retryQboRejected(projectId: number): Observable<QboSyncRun> {
    return this.http.post<QboSyncRun>(`${this.base}${this.endpoints.QBO_SYNC_RETRY(projectId)}`, {});
  }

  /** Files the document on the line first — the API requires its id on Actual and Committed posts (FR-08). */
  private postEntry(lineId: string, file: File | null, body: Record<string, unknown>): Observable<JcTransaction> {
    const attach: Observable<number | null> = file ? this.uploadAttachment(lineId, file) : of(null);
    return attach.pipe(
      switchMap(attachmentId =>
        this.http.post<ApiEnvelope<PostedEntryResult>>(`${this.base}${this.endpoints.POST_ENTRY_POSTED}`, {
          clientRequestId: crypto.randomUUID(),
          ...body,
          attachmentId,
        }),
      ),
      map(response => {
        const entry = response.data?.entry;
        if (!entry) {
          throw new Error('The entry was not returned');
        }
        return mapEntryWithContext(entry);
      }),
    );
  }

  private uploadAttachment(lineId: string, file: File): Observable<number> {
    const form = new FormData();
    form.append('ledgerId', lineId);
    form.append('file', file, file.name);
    return this.http
      .post<ApiEnvelope<LedgerAttachment>>(`${this.base}${this.endpoints.LINE_DOCUMENT_UPLOAD}`, form)
      .pipe(
        map(response => {
          const id = response.data?.attachmentId;
          if (!id) {
            throw new Error('The document upload did not return an id');
          }
          return id;
        }),
      );
  }

  private entryReviewChanges(entry: LedgerEntry): Observable<JcReviewChange[]> {
    const params = new HttpParams().set('entryId', String(entry.entryId));
    return this.http
      .get<ApiEnvelope<AuditLog[]>>(`${this.base}${this.endpoints.ENTRY_AUDIT_LOGS}`, { params })
      .pipe(
        map(response => mapReviewChanges(entry, response.data ?? [])),
        // History still renders without the review log.
        catchError(() => of([])),
      );
  }

  private ledgerEntries(lineId: string, excludeEntityType?: string): Observable<LedgerEntry[]> {
    let params = new HttpParams().set('ledgerId', lineId).set('pageNumber', '1').set('pageSize', '200');
    if (excludeEntityType) {
      params = params.set('excludeEntityType', excludeEntityType);
    }
    return this.http
      .get<ApiEnvelope<LedgerPage<LedgerEntry>>>(`${this.base}${this.endpoints.LINE_TRANSACTIONS}`, { params })
      .pipe(map(response => response.data?.items ?? []));
  }
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T | null;
}

interface LedgerPage<T> {
  items: T[];
  totalPages?: number;
}

/** The eight-tile band every rollup level carries (JobCostRollupTotalsResponse). */
interface JobCostBand {
  contractPrice: number;
  changeOrderMarkup: number;
  changeOrderCost: number;
  originalJobCostBudget: number;
  actual: number;
  committed: number;
  openCommitted: number;
  accrued: number;
  costVariance: number;
  projectedCost: number;
  overBudgetLineCount: number;
  pendingActual?: number | null;
  pendingEntryCount?: number | null;
}

interface JobCostLanding {
  projects: LedgerPage<JobCostLandingRow>;
}

interface JobCostLandingRow extends JobCostBand {
  projectId: number;
  projectTitle: string;
  projectStatus: string | null;
  hasBudget: boolean;
  lineCount: number;
  inboxPendingCount: number;
  inboxPendingAmount: number;
}

function mapLandingRow(row: JobCostLandingRow): JobCostingProjectSummary {
  return {
    projectId: row.projectId,
    projectName: row.projectTitle,
    status: mapProjectStatus(row.projectStatus),
    sell: num(row.contractPrice),
    budgetCost: num(row.originalJobCostBudget),
    changeOrderSell: num(row.changeOrderMarkup),
    changeOrderCost: num(row.changeOrderCost),
    committed: num(row.committed),
    openCommitted: num(row.openCommitted),
    accrued: num(row.accrued),
    actual: num(row.actual),
    costVariance: num(row.costVariance),
    marginVariance: null,
    projectedCost: num(row.projectedCost),
    pending: num(row.pendingActual),
    pendingCount: row.pendingEntryCount ?? 0,
    overCount: row.overBudgetLineCount ?? 0,
    reviewCount: row.inboxPendingCount ?? 0,
    reviewAmount: num(row.inboxPendingAmount),
  };
}

function mapProjectStatus(status: string | null): JcProjectStatus {
  switch ((status ?? '').toUpperCase()) {
    case 'COMPLETED':
    case 'COMPLETE':
    case 'CLOSED':
      return 'Complete';
    case 'UPCOMING':
      return 'Upcoming';
    default:
      return 'In Process';
  }
}

interface JobCostRollup {
  projectId: number;
  projectTitle: string;
  hasBudget?: boolean;
  inboxPendingCount?: number;
  inboxPendingAmount?: number;
  budgetSource?: {
    dateProduction?: string | null;
    estimates?: { estimateName?: string | null; setName?: string | null; version: number }[] | null;
  } | null;
  sections: JobCostRollupSection[];
}

interface JobCostRollupSection {
  scopeTypeId: number | null;
  code: string | null;
  name: string;
  lines: JobCostRollupLine[];
}

interface JobCostRollupLine {
  ledgerId: number;
  projectId: number;
  costCodeId: number;
  costCode: string;
  costCodeName: string;
  costCodeType: string | null;
  scopeTypeId?: number | null;
  /** Original Contract Price (client $, excluding change orders). */
  contractPrice: number;
  changeOrderMarkup: number;
  changeOrderCost: number;
  originalJobCostBudget: number;
  committedAmount: number;
  openCommitted: number;
  accruedAmount: number;
  actualAmount: number;
  pendingActualAmount: number;
  isComplete: boolean;
  isUnbudgeted: boolean;
  isAdded: boolean;
  attachmentCount: number;
}

function mapRollup(rollup: JobCostRollup | null, projectId: number): JobCostingProjectDetail {
  const mapped = (rollup?.sections ?? []).map(mapSection);
  // Costs on a code with no budget line sit together in one Unbudgeted costs section (§3).
  const unbudgeted = mapped.flatMap(section => section.lines.filter(line => line.isUnbudgeted));
  const sections = mapped
    .map(section => ({ ...section, lines: section.lines.filter(line => !line.isUnbudgeted) }))
    .filter(section => section.lines.length);
  if (unbudgeted.length) {
    sections.push({ sectionId: 'unbudgeted', code: '', name: 'Unbudgeted costs', lines: unbudgeted, isUnbudgeted: true });
  }
  const lines = sections.flatMap(section => section.lines);
  const complete = lines.length > 0 && lines.every(line => line.isComplete);
  return {
    projectId: rollup?.projectId ?? projectId,
    projectName: rollup?.projectTitle ?? '',
    status: complete ? 'Complete' : 'In Process',
    budgetSource: mapBudgetSource(rollup?.budgetSource),
    sections,
    inboxPendingCount: rollup?.inboxPendingCount ?? 0,
    inboxPendingAmount: num(rollup?.inboxPendingAmount),
    hasBudget: rollup?.hasBudget ?? lines.some(line => !line.isUnbudgeted),
  };
}

function mapBudgetSource(source: JobCostRollup['budgetSource']): JcBudgetSource | null {
  if (!source) {
    return null;
  }
  return {
    startedProductionAt: source.dateProduction ?? null,
    sheets: (source.estimates ?? []).map(estimate => ({
      name: [estimate.estimateName, estimate.setName].filter(Boolean).join(' · ') || 'Estimate',
      version: `v${estimate.version}`,
    })),
    estimateUrl: null,
  };
}

function mapSection(section: JobCostRollupSection): JcSection {
  const sectionId = String(section.scopeTypeId ?? 'unsectioned');
  return {
    sectionId,
    code: section.code ?? '',
    name: section.name || 'Unsectioned',
    lines: section.lines.map(line => mapLine(line, sectionId)),
  };
}

function mapLine(line: JobCostRollupLine, sectionId: string): JcLine {
  return {
    lineId: String(line.ledgerId),
    lineEntityId: line.ledgerId,
    projectId: line.projectId,
    costCodeId: line.costCodeId,
    sectionId,
    costCode: line.costCode,
    description: line.costCodeName,
    costType: mapCostType(line.costCodeType),
    baselineBudget: num(line.contractPrice),
    changeOrderBudget: num(line.changeOrderMarkup),
    estimatedCost: num(line.originalJobCostBudget),
    changeOrderCost: num(line.changeOrderCost),
    actual: num(line.actualAmount),
    poCommitted: num(line.committedAmount),
    openCommitted: num(line.openCommitted),
    accrued: num(line.accruedAmount),
    pendingAmount: num(line.pendingActualAmount),
    isComplete: line.isComplete,
    isAdded: line.isAdded,
    isUnbudgeted: line.isUnbudgeted,
    documentCount: line.attachmentCount ?? 0,
  };
}

const emptyQboPreview: QboSyncPreview = {
  eligibleCount: 0,
  eligibleAmount: 0,
  heldPendingCount: 0,
  heldPendingAmount: 0,
  heldReviewCount: 0,
  heldReviewAmount: 0,
  heldAccruedCount: 0,
  heldAccruedAmount: 0,
  unmappedCount: 0,
  runs: [],
};

interface LedgerAttachment {
  attachmentId: number;
  originalFileName?: string | null;
  name?: string | null;
  fullUrlWithSAS?: string | null;
  fullUrlCdn?: string | null;
  fullUrlBase?: string | null;
  dateCreated?: string | null;
  attachmentType?: string | null;
  entityType?: string | null;
  entityId?: number | null;
}

/** JobCostLedgerEntryResponse. */
interface LedgerEntry {
  entryId: number;
  ledgerId: number;
  /** BUDGET · COMMITMENT · ACTUAL · ACCRUAL. */
  entryClass: string;
  /** ACTUAL · CREDIT · COMMITMENT · ACCRUAL · TIME · BUDGET · REVERSAL. */
  entryType?: string | null;
  amount: number;
  markUpAmount?: number | null;
  qty?: number | null;
  documentDate: string;
  entityType: string;
  entityId?: number | null;
  documentType: string;
  sourceLabel?: string | null;
  status: string;
  vendor?: string | null;
  externalRef?: string | null;
  description?: string | null;
  memo?: string | null;
  postedByDisplayName?: string | null;
  reviewedByDisplayName?: string | null;
  reviewedAt?: string | null;
  attachments?: LedgerAttachment[];
  isReversal?: boolean;
  reversesEntryId?: number | null;
  reversedByEntryId?: number | null;
  statusReason?: string | null;
  serviceStartDate?: string | null;
  serviceEndDate?: string | null;
  /** ACCRUAL: OPEN · INVOICED · REVERSED. */
  accrualStatus?: string | null;
  matchedAccrualEntryId?: number | null;
  matchedActualEntryId?: number | null;
  appliedToEntryId?: number | null;
  creditsEntryId?: number | null;
  editedFromEntryId?: number | null;
  editedByEntryId?: number | null;
  remainingCommitment?: number | null;
  overBudgetReason?: string | null;
  workerName?: string | null;
  hours?: number | null;
  rate?: number | null;
  isPaid?: boolean;
  datePaid?: string | null;
  isEditable?: boolean;
  isReversible?: boolean;
  workerUserObjectId?: string | null;
  workerDisplayName?: string | null;
  workerHours?: number | null;
  appliedBaseWage?: number | null;
  appliedBurdenPct?: number | null;
  laborCost?: number | null;
  timeEntryId?: number | null;
}

interface LedgerNote {
  noteId: number;
  text?: string | null;
  dateCreated: string;
  authorUser?: { firstName?: string | null; lastName?: string | null } | null;
}

interface PostedEntryResult {
  entry?: LedgerEntry | null;
}

interface EditEntryResult {
  reversalEntry?: LedgerEntry | null;
  replacementEntry?: LedgerEntry | null;
}

interface AuditLog {
  action: string;
  eventAtUtc: string;
  actorUserName?: string | null;
  changes?: { fieldPath: string; oldValue?: string | null; newValue?: string | null }[] | null;
}

function num(value: number | null | undefined): number {
  return Number(value) || 0;
}

function toId(value: string | null | undefined): number | null {
  return value ? Number(value) || null : null;
}

/** The accrual choice the API requires on an actual when the line has open accruals; credits skip it (FR-19). */
function accrualChoice(credit: boolean, choice: string | null | undefined, matched: number | null): Record<string, unknown> {
  if (credit || !choice) {
    return {};
  }
  return matched ? { matchedAccrualEntryId: matched } : { notRelatedToAccrual: true };
}

/** The API holds Paid / Unpaid; the due date rides in the memo. */
function dueNote(payment: 'Paid' | 'Unpaid', dueDate: string | null | undefined): string {
  return payment === 'Unpaid' && dueDate ? `Due ${dueDate}` : '';
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Edits are a reversal plus a replacement on the API; the Costs tab shows only the replacement, noting
 * "Edited — amount was $X" (FR-07, FR-20). Everything else maps one entry to one row.
 */
function mapEntries(entries: LedgerEntry[]): JcTransaction[] {
  const byId = new Map(entries.map(item => [item.entryId, item]));
  const edited = new Set(entries.filter(item => item.editedByEntryId).map(item => item.entryId));
  return entries
    .filter(entry => !edited.has(entry.entryId) && !(entry.reversesEntryId && edited.has(entry.reversesEntryId)))
    .map(entry => mapEntryWithContext(entry, byId));
}

function mapEntryWithContext(entry: LedgerEntry, byId: Map<number, LedgerEntry> = new Map()): JcTransaction {
  const attachment = entry.attachments?.[0];
  const type = mapTxnType(entry);
  const bucket = mapBucket(entry);
  const labor = type === 'Labor';
  // the API reports every LABOR document as entryType TIME; only rows not produced by an approved timesheet are manual
  const timesheetLabor = labor && isTimesheetEntry(entry);
  const manualLabor = labor && !timesheetLabor;
  // one hours figure for the column and the rate, so rate × hours shows the amount
  const hours = entry.hours ?? entry.workerHours ?? null;
  const credit = type === 'Credit';
  const payment = bucket === 'Actual' && !credit && !labor && !entry.isReversal ? (entry.isPaid ? 'Paid' : 'Unpaid') : null;
  const reversal = entry.reversedByEntryId ? byId.get(entry.reversedByEntryId) : undefined;
  const original = entry.editedFromEntryId ? byId.get(entry.editedFromEntryId) : undefined;
  const invoicedBy = reversal?.matchedActualEntryId ? byId.get(reversal.matchedActualEntryId) : undefined;
  const status = mapDisplayStatus(entry);
  return {
    transactionId: String(entry.entryId),
    lineId: String(entry.ledgerId),
    date: entry.documentDate,
    postingDate: entry.documentDate,
    documentDate: entry.documentDate,
    payment,
    dueDate: dueFromMemo(entry.memo),
    hours,
    rate: entry.rate ?? (timesheetLabor ? burdenedRate(entry, hours) : null),
    type,
    reference: displayReference(entry, type),
    vendor: entry.vendor || entry.workerName || entry.workerDisplayName || entry.postedByDisplayName || '',
    amount: num(entry.amount),
    bucket,
    status,
    note: entryNote(entry, byId),
    attachment: attachment ? mapAttachment(attachment) : null,
    postedBy: entry.postedByDisplayName || null,
    reviewedBy: entry.reviewedByDisplayName || null,
    reviewedAt: entry.reviewedAt || null,
    entityType: entry.entityType,
    isReversal: !!entry.isReversal,
    reversedRef: status === 'Reversed' && entry.reversedByEntryId ? `REV-${entry.reversedByEntryId}` : null,
    matchedInvoiceRef: status === 'Invoiced' ? invoicedBy?.externalRef || (reversal?.matchedActualEntryId ? `#${reversal.matchedActualEntryId}` : null) : null,
    appliedToEntryId: entry.appliedToEntryId != null ? String(entry.appliedToEntryId) : null,
    appliedAmount: entry.remainingCommitment != null ? num(entry.amount) - num(entry.remainingCommitment) : null,
    creditsEntryId: entry.creditsEntryId != null ? String(entry.creditsEntryId) : null,
    editedNote: entry.editedFromEntryId ? `Edited — amount was ${original ? formatMoney(num(original.amount)) : 'changed'}` : null,
    timesheetId: timesheetLabor && entry.timeEntryId != null ? String(entry.timeEntryId) : null,
    workerUserObjectId: entry.workerUserObjectId ?? null,
    isManualEntry: manualLabor,
    serviceStart: entry.serviceStartDate ?? null,
    serviceEnd: entry.serviceEndDate ?? null,
    isEditable: !!entry.isEditable,
    isReversible: !!entry.isReversible,
  };
}

function displayReference(entry: LedgerEntry, type: JcTransaction['type']): string {
  if (entry.isReversal) {
    return `REV-${entry.entryId}`;
  }
  if (entry.externalRef) {
    return entry.externalRef;
  }
  switch (type) {
    case 'Accrual':
      return `ACR-${entry.entryId}`;
    case 'Credit':
      return `CR-${entry.entryId}`;
    case 'PO':
      return `PO-${entry.entryId}`;
    case 'Labor':
      return isTimesheetEntry(entry) ? 'Timesheet' : 'Manual entry';
    default:
      return entry.sourceLabel || titleCase(entry.documentType) || '—';
  }
}

/** Provenance the record carries: accrual relief, service period, over-budget reason, then the memo. */
function isTimesheetEntry(entry: LedgerEntry): boolean {
  return (entry.entityType ?? '').toUpperCase() === 'TRADE_SEGMENT';
}

/**
 * Fully burdened $/hr of a timesheet row: the segment's labor cost over its hours (APPLIED_BASE_WAGE is the
 * unburdened wage). Null when the API withholds wage fields (no LABOR_COST_VIEW).
 */
function burdenedRate(entry: LedgerEntry, hours: number | null): number | null {
  if (entry.laborCost != null && num(hours) > 0) {
    return Math.round((num(entry.laborCost) / num(hours)) * 100) / 100;
  }
  if (entry.appliedBaseWage != null) {
    return Math.round(num(entry.appliedBaseWage) * (1 + num(entry.appliedBurdenPct) / 100) * 100) / 100;
  }
  return null;
}

function entryNote(entry: LedgerEntry, byId: Map<number, LedgerEntry>): string | null {
  const parts: string[] = [];
  if (entry.matchedAccrualEntryId) {
    const accrual = byId.get(entry.matchedAccrualEntryId);
    if (accrual) {
      const diff = Math.round((num(entry.amount) - num(accrual.amount)) * 100) / 100;
      const diffText = Math.abs(diff) > 0.005 ? `, difference ${diff < 0 ? '-' : ''}${formatMoney(diff)}` : '';
      parts.push(`Matches ACR-${accrual.entryId} — accrual relieved (accrued ${formatMoney(num(accrual.amount))} vs actual ${formatMoney(num(entry.amount))}${diffText}).`);
    } else {
      parts.push(`Matches ACR-${entry.matchedAccrualEntryId} — accrual relieved.`);
    }
  }
  if (entry.serviceStartDate && entry.serviceEndDate) {
    parts.push(`Service ${entry.serviceStartDate} to ${entry.serviceEndDate}.`);
  }
  if (entry.overBudgetReason) {
    parts.push(`Over budget: ${entry.overBudgetReason}`);
  }
  if (entry.isReversal && entry.statusReason) {
    parts.push(entry.statusReason);
  }
  // the timesheet producer's memo ("Approved timesheet (time entry …)") repeats the linked name, hours and rate
  const text = isTimesheetEntry(entry) ? entry.description : entry.memo || entry.description;
  if (text && !/^Due \d{4}-\d{2}-\d{2}$/.test(text)) {
    parts.push(text);
  }
  return parts.join(' ') || null;
}

function dueFromMemo(memo: string | null | undefined): string | null {
  return memo?.match(/Due (\d{4}-\d{2}-\d{2})/)?.[1] ?? null;
}

function mapDisplayStatus(entry: LedgerEntry): JcTransaction['status'] {
  switch ((entry.status ?? '').toUpperCase()) {
    case 'REJECTED':
      return 'Closed';
    case 'PENDING':
      return 'Pending';
  }
  if (entry.isReversal) {
    return 'Posted';
  }
  switch ((entry.entryClass ?? '').toUpperCase()) {
    case 'COMMITMENT':
      // A PO is a memo record: a reversal only ever means Cancel PO (FR-20).
      return entry.reversedByEntryId ? 'Canceled' : 'Open';
    case 'ACCRUAL':
      switch ((entry.accrualStatus ?? '').toUpperCase()) {
        case 'INVOICED':
          return 'Invoiced';
        case 'REVERSED':
          return 'Reversed';
        default:
          return entry.reversedByEntryId ? 'Reversed' : 'Accrued';
      }
  }
  if (entry.reversedByEntryId) {
    return 'Reversed';
  }
  return entry.isPaid ? 'Paid' : 'Posted';
}

function mapTxnType(entry: LedgerEntry): JcTransaction['type'] {
  const entryType = (entry.entryType ?? '').toUpperCase();
  if (entry.isReversal || entryType === 'REVERSAL') {
    return 'Reversal';
  }
  const entity = (entry.entityType ?? '').toUpperCase();
  const document = (entry.documentType ?? '').toUpperCase();
  switch (entryType) {
    case 'COMMITMENT':
      return 'PO';
    case 'ACCRUAL':
      return 'Accrual';
    case 'TIME':
      return 'Labor';
    case 'CREDIT':
      return 'Credit';
  }
  if (entity === 'CHANGE_ORDER_LINE_ITEM' || document === 'CHANGE_ORDER') {
    return 'CO';
  }
  if (entity === 'TRADE_SEGMENT' || document === 'LABOR') {
    return 'Labor';
  }
  switch (document) {
    case 'BILL':
      return 'Bill';
    case 'INVOICE':
      return 'Invoice';
    case 'EXPENSE':
      return 'Expense';
    case 'RECEIPT':
      return 'Receipt';
    case 'CREDIT':
      return 'Credit';
    default:
      return 'Manual';
  }
}

function mapBucket(entry: LedgerEntry): JcTransaction['bucket'] {
  switch ((entry.entryClass ?? '').toUpperCase()) {
    case 'ACCRUAL':
      return 'Accrued';
    case 'BUDGET':
      return 'Budget';
    case 'COMMITMENT':
      return 'Committed';
    default:
      return 'Actual';
  }
}

function mapHistory(entries: LedgerEntry[], reviewChanges: JcReviewChange[]): JcLineHistory {
  const isCo = (entry: LedgerEntry) => (entry.entityType ?? '').toUpperCase() === 'CHANGE_ORDER_LINE_ITEM';
  const isManual = (entry: LedgerEntry) => (entry.entityType ?? '').toUpperCase() === 'MANUAL';
  const budget = entries.filter(entry => entry.entryClass === 'BUDGET' || entry.entityType === 'EST_LINEITEM');
  const activity = entries.filter(entry => entry.entryClass !== 'BUDGET' && entry.entityType !== 'EST_LINEITEM');
  const revised = budget.reduce((sum, entry) => sum + num(entry.amount), 0);
  const revisedSell = budget.reduce((sum, entry) => sum + num(entry.markUpAmount), 0);
  return {
    budgetRecords: budget.map(entry => ({
      label: entry.isReversal ? 'Reversal' : isCo(entry) ? entry.externalRef || 'Change order' : isManual(entry) ? 'Added' : 'Baseline',
      source: isCo(entry)
        ? `Approved change order${entry.memo ? ` — ${entry.memo}` : ''}`
        : isManual(entry)
          ? entry.description || 'Added in production by user'
          : entry.description || entry.memo || 'From estimate at Start Production',
      amount: num(entry.amount),
      sellAmount: entry.markUpAmount != null ? num(entry.markUpAmount) : null,
      status: isCo(entry) ? `Approved ${entry.documentDate}` : mapEntryStatus(entry.status),
      attachment: entry.attachments?.[0] ? mapAttachment(entry.attachments[0]) : null,
    })),
    revisedBudget: budget.length ? revised : null,
    revisedSell: budget.length ? revisedSell : null,
    reviewChanges,
    activity: mapEntries(activity),
  };
}

const REVIEW_FIELD_LABELS: Record<string, string> = {
  documentDate: 'Document date',
  projectId: 'Project',
  vendor: 'Vendor',
  memo: 'Memo',
  totalAmount: 'Total',
  taxAmount: 'Tax',
  isCredit: 'Credit / return',
};

/** The Cost Inbox item's REVIEW_CHANGE log carried onto the posted record (FR-13, R-22a). */
function mapReviewChanges(entry: LedgerEntry, logs: AuditLog[]): JcReviewChange[] {
  const reference = entry.externalRef || `#${entry.entryId}`;
  return logs
    .filter(log => (log.action ?? '').toUpperCase() === 'COST_INBOX_ITEM.REVIEW_CHANGE')
    .flatMap(log =>
      (log.changes ?? []).map(change => ({
        reference,
        inboxRef: null,
        field: reviewFieldLabel(change.fieldPath),
        submitted: change.oldValue ?? '—',
        changedTo: change.newValue ?? '—',
        by: log.actorUserName || 'User',
        at: (log.eventAtUtc ?? '').slice(0, 10),
      })),
    );
}

function reviewFieldLabel(fieldPath: string): string {
  const key = (fieldPath ?? '').split('.').pop() ?? '';
  const camel = key.charAt(0).toLowerCase() + key.slice(1);
  if (REVIEW_FIELD_LABELS[camel]) {
    return REVIEW_FIELD_LABELS[camel];
  }
  if (/costcode/i.test(fieldPath)) {
    return 'Cost code';
  }
  if (/amount/i.test(fieldPath)) {
    return 'Amount';
  }
  return fieldPath;
}

function mapDocuments(files: LedgerAttachment[], entries: LedgerEntry[]): JcDocument[] {
  const entriesById = new Map(entries.map(entry => [entry.entryId, entry]));
  const byId = new Map<string, JcDocument>();
  for (const file of files) {
    const entry = file.entityType === 'JOB_COST_LEDGER_ENTRY' && file.entityId
      ? entriesById.get(file.entityId) ?? null
      : null;
    byId.set(String(file.attachmentId), mapAttachmentDoc(file, entry));
  }
  for (const entry of entries) {
    for (const file of entry.attachments ?? []) {
      const id = String(file.attachmentId);
      const existing = byId.get(id);
      if (!existing || existing.amount == null) {
        byId.set(id, mapAttachmentDoc(file, entry));
      }
    }
  }
  return [...byId.values()];
}

function mapAttachmentDoc(file: LedgerAttachment, entry: LedgerEntry | null): JcDocument {
  return {
    documentId: String(file.attachmentId),
    date: file.dateCreated || entry?.documentDate || '',
    kind: titleCase(entry?.documentType || file.attachmentType) || 'Document',
    reference: entry?.externalRef ?? null,
    vendor: entry?.vendor ?? null,
    amount: entry ? num(entry.amount) : null,
    status: entry ? mapEntryStatus(entry.status) : null,
    attachment: mapAttachment(file),
  };
}

function mapAttachment(file: LedgerAttachment): JcAttachment {
  return {
    attachmentId: String(file.attachmentId),
    fileName: file.originalFileName || file.name || 'Document',
    url: file.fullUrlWithSAS || file.fullUrlCdn || file.fullUrlBase || null,
  };
}

function mapNote(note: LedgerNote | null): JcNote {
  const author = [note?.authorUser?.firstName, note?.authorUser?.lastName].filter(Boolean).join(' ');
  return {
    noteId: String(note?.noteId ?? ''),
    author: author || 'User',
    createdAt: note?.dateCreated ?? '',
    text: note?.text ?? '',
  };
}

function mapEntryStatus(status: string): JcTransaction['status'] {
  switch ((status ?? '').toUpperCase()) {
    case 'POSTED':
      return 'Posted';
    case 'REJECTED':
      return 'Closed';
    default:
      return 'Pending';
  }
}

function titleCase(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  if (!text) {
    return '';
  }
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function mapCostType(type: string | null): JcCostType {
  switch ((type ?? '').toUpperCase()) {
    case 'LABOR':
      return 'Labor';
    case 'SUBCONTRACT':
    case 'SUBCONTRACTOR':
      return 'Subcontractor';
    case 'MATERIAL':
      return 'Material';
    default:
      return 'Other';
  }
}

function errorText(err: HttpErrorResponse): string {
  const body = err.error;
  if (typeof body === 'string') {
    return body;
  }
  if (body && typeof body === 'object') {
    const typed = body as { message?: string; errorCode?: string; errors?: string[] };
    return [typed.message, typed.errorCode, ...(typed.errors ?? [])].filter(Boolean).join(' ');
  }
  return err.message || '';
}

function isAlreadyReversedError(err: HttpErrorResponse): boolean {
  const text = errorText(err).toUpperCase();
  return err.status === 409 && (text.includes('ALREADY_REVERSED') || text.includes('ALREADY REVERSED'));
}
